import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Box, Typography, Button, Alert } from "@mui/material";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import DynamicForm from "../../components/ui/dynamicForm/dynamicForm.component";
import { useCRUD } from "../../api/crud";
import { PARTS_ENDPOINT, ORDERED_PARTS_ENDPOINT, USED_PARTS_ENDPOINT, JOB_TASKS_ENDPOINT, NUMERATION_SCHEMA_ENDPOINT } from "../../consts";
import { SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import { useAuthContext, useHasPermission } from "../../contexts/auth";
import { ApiFieldConfigMap, DynamicFieldsConfig } from "../../types/dynamicForm";

export const PartDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuthContext();
  const { loading, error, remove } = useCRUD(PARTS_ENDPOINT);
  const [updatePartOpened, setUpdatePartOpened] = useState<boolean>(false);
  const [deletePartOpened, setDeletePartOpened] = useState<boolean>(false);
  const [addOrderedPartOpened, setAddOrderedPartOpened] = useState<boolean>(false);
  const [addUsedPartOpened, setAddUsedPartOpened] = useState<boolean>(false);

  const canChangePart = useHasPermission("service.change_part");
  const canDeletePart = useHasPermission("service.delete_part");
  const canAddOrderedPart = useHasPermission("service.add_orderedpart");
  const canAddUsedPart = useHasPermission("service.add_usedpart");

  const isOnOrderedPartsTab = location.pathname.includes('/ordered-parts');
  const isOnUsedPartsTab = location.pathname.includes('/used-parts');

  const handleDeletePart = async () => {
    if (loading || !id) {
      return;
    }

    const result = await remove({ id });

    if (result?.success) {
      enqueueSnackbar(t('Part deleted successfully'), SUCCESS_SNACKBAR);
      setDeletePartOpened(false);
      navigate('/service/stock-status');
    } else {
      enqueueSnackbar(t('Failed to delete part'), ERROR_SNACKBAR);
    }
  };

  const handleOrderedPartAdded = () => {
    setAddOrderedPartOpened(false);
    window.dispatchEvent(new Event("orderedPartAdded"));
    window.dispatchEvent(new Event("partUpdated"));
  };

  const handleUsedPartAdded = () => {
    setAddUsedPartOpened(false);
    window.dispatchEvent(new Event("usedPartAdded"));
    window.dispatchEvent(new Event("partUpdated"));
  };

  const actions: Action[] = [
    ...(isOnOrderedPartsTab && canAddOrderedPart ? [{
      icon: <AddIcon />,
      label: t("Add new ordered part"),
      onClick: () => {
        setAddOrderedPartOpened(true);
      },
      disabled: false
    }] : []),
    ...(isOnUsedPartsTab && canAddUsedPart ? [{
      icon: <AddIcon />,
      label: t("Add new used part"),
      onClick: () => {
        setAddUsedPartOpened(true);
      },
      disabled: false
    }] : []),
    {
      icon: <EditIcon />,
      label: t("Update part"),
      onClick: () => {
        setUpdatePartOpened(true);
      },
      disabled: !canChangePart,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete part"),
      onClick: () => {
        setDeletePartOpened(true);
      },
      color: "var(--errorColor)",
      disabled: !canDeletePart,
    },
  ];

  const orderedPartApiFields: ApiFieldConfigMap = {
    numeration_schema_id: {url: NUMERATION_SCHEMA_ENDPOINT, field: "name"},
    part: { url: PARTS_ENDPOINT, field: "name" },
    ordered_by_id: { url: "/users/users/", field: "email" },
  };

  const orderedPartDynamicFields: DynamicFieldsConfig = {
    part: [
      { dependsOn: "part", value: id, action: "disable" },
    ],
    number: [
      { dependsOn: "numeration_schema_id", value: "ANY", action: "disable" },
      { dependsOn: "numeration_schema_id", valueSource: `${NUMERATION_SCHEMA_ENDPOINT}{id}/next/` },
    ],
  };

  const usedPartApiFields: ApiFieldConfigMap = {
    ordered_part: {
      url: ORDERED_PARTS_ENDPOINT,
      field: "display_name",
      queryParams: { status: "O,D" },
    },
    job_task: { url: JOB_TASKS_ENDPOINT, field: "display_name" },
  };

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  const deleteModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('Deleting this part will permanently remove it from the system. This action cannot be undone.')}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t('This action cannot be undone. All associated data will be permanently deleted.')}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDeletePart}
        disabled={loading}
      >
        {loading ? t('Processing...') : t('Confirm deletion')}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={updatePartOpened}
        setOpened={setUpdatePartOpened}
        title={t("Update part")}
        content={
          <DynamicForm
            url={PARTS_ENDPOINT}
            id={id}
            onSuccess={() => {
              setUpdatePartOpened(false);
              window.dispatchEvent(new Event("partUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={deletePartOpened}
        setOpened={setDeletePartOpened}
        title={t("Delete part")}
        content={deleteModalContent}
      />
      <CustomModal
        opened={addOrderedPartOpened}
        setOpened={setAddOrderedPartOpened}
        title={t("Add new ordered part")}
        content={
          <DynamicForm
            url={ORDERED_PARTS_ENDPOINT}
            onSuccess={handleOrderedPartAdded}
            apiFields={orderedPartApiFields}
            dynamicFields={orderedPartDynamicFields}
            initialValues={id ? { part: id, ordered_by_id: currentUser?.id } : { ordered_by_id: currentUser?.id }}
          />
        }
      />
      <CustomModal
        opened={addUsedPartOpened}
        setOpened={setAddUsedPartOpened}
        title={t("Add new used part")}
        content={
          <DynamicForm
            url={USED_PARTS_ENDPOINT}
            onSuccess={handleUsedPartAdded}
            apiFields={usedPartApiFields}
          />
        }
      />
    </>
  );
};
